/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential.
 *
 * Invite with documents (approved 14/10/2026): the held invitation on the case,
 * as the officer reads it (the case view notice and the pipeline chip).
 * Owner 15/10/2026: once released, an invitation email that was refused or
 * bounced is never shown as sent; the notice says it was not delivered and to
 * resend it. No dashes in any text here.
 * Audit batch (owner 16/10/2026): the notice carries a Resend invitation
 * button (S1, S8), the alerts and case notes name that button, a directly sent
 * invitation whose email was refused gets the same notice and a case note (S9),
 * and the dialog never says sent unless the email was accepted (S2, S5).
 */

// The one action every not delivered message names: the button on the notice.
export const RESEND_LABEL = 'Resend invitation';
// Beside the button, disabled, when the case has no client email (owner 16/10/2026).
export const RESEND_NO_EMAIL = 'Add the client\u2019s email first';
export const RESEND_ACTION = `Press ${RESEND_LABEL} once the address is confirmed.`;
export const RESEND_FROM_CASE = `Open the case and press ${RESEND_LABEL} once the address is confirmed.`;
export const EMAIL_FAILED_LINE = 'The invitation email could not be delivered';
export function inviteHoldState(row) {
  const cd = (row && row.collected_data) || {};
  const h = cd.inviteHold;
  if (!h || !row || row.status !== 'invited') return null;
  if (h.held === true) return { held: true, until: h.until };
  return { held: false, reason: h.reason, at: h.releasedAt };
}

const hm = (v) => { const d = new Date(v); return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }); };
const NOT_DELIVERED = ['rejected', 'bounced', 'complained'];

// The invitation email did not reach the client (refused when sent, or bounced later).
export function inviteEmailNotDelivered(row) {
  const ie = (row && row.collected_data && row.collected_data.inviteEmail) || null;
  if (!ie) return null;
  if (ie.sent === false || NOT_DELIVERED.includes(String(ie.deliveryState || ''))) return ie;
  return null;
}

const whyOf = (failed) => String(failed.bounceReason || failed.error || '').replace(/\s+/g, ' ').trim().slice(0, 140);

/** The case note when a directly sent invitation's email was refused (S9). */
export function directNotDeliveredNote({ email, emailError }) {
  const why = String(emailError || 'no reason given').replace(/\s+/g, ' ').trim().slice(0, 160);
  return `The invitation email${email ? ' to ' + email : ''} could not be delivered (${why}). The client has not received the link. ${RESEND_FROM_CASE}`;
}

// { text, warn } for the case view notice, or null when there is nothing to say.
export function inviteHoldNotice(row) {
  const st = inviteHoldState(row);
  if (!st) {
    // S9: an invitation sent directly (no hold) whose email was refused, or bounced later.
    const failed = row && row.status === 'invited' && !(row.collected_data && row.collected_data.inviteHold) ? inviteEmailNotDelivered(row) : null;
    if (!failed) return null;
    const to = failed.to || row.contact_email || '';
    const why = whyOf(failed);
    return { st: { held: false, reason: 'direct' }, warn: true, notDelivered: true, text: `${EMAIL_FAILED_LINE}${to ? ' to ' + to : ''}${why ? ' (' + why + ')' : ''}. The client has not received the link. ${RESEND_ACTION}` };
  }
  const failed = !st.held ? inviteEmailNotDelivered(row) : null;
  if (failed) {
    const to = failed.to || (row && row.contact_email) || '';
    const why = whyOf(failed);
    return { st, warn: true, notDelivered: true, text: `The invitation was released at ${hm(st.at)}, but the email${to ? ' to ' + to : ''} could not be delivered${why ? ' (' + why + ')' : ''}. The client has not received the link. ${RESEND_ACTION}` };
  }
  const warn = !st.held && (st.reason === 'time_limit' || st.reason === 'engine_failed');
  const text = st.held
    ? `Invitation held until the documents the firm supplied have been read. It is sent automatically when the reading finishes, or at ${hm(st.until)} at the latest.`
    : st.reason === 'time_limit' ? `The invitation was sent at ${hm(st.at)} because the time limit for reading the documents ran out. The reading carries on; the client may start before it finishes.`
    : st.reason === 'engine_failed' ? `The reading of the documents the firm supplied did not complete, so the invitation was sent at ${hm(st.at)} without waiting. Press Reprocess to read them.`
    : st.reason === 'officer' ? `The invitation was sent at ${hm(st.at)}, before the documents had been read.`
    : `The invitation was sent at ${hm(st.at)}, once the documents had been read.`;
  return { st, warn, notDelivered: false, text };
}

/**
 * S5: what the Invite Client dialog may say about a held invitation it asked
 * to be sent (send-held, or the release record-documents made when the reading
 * did not start). outcome: { emailSent } from the server, { error } when the
 * call failed, or null when nothing was asked. Sent is said only when the email
 * was accepted.
 */
export function heldSendOutcome(outcome) {
  if (!outcome) return { status: null, line: '' };
  if (outcome.emailSent === true) return { status: 'success', line: 'The invitation has been sent without waiting. ' };
  if (outcome.emailSent === false) return { status: 'email_failed', line: `The invitation was released, but the email could not be delivered. ${RESEND_FROM_CASE} ` };
  const why = String(outcome.error || 'no reason given').replace(/\s+/g, ' ').trim().slice(0, 160);
  return { status: 'not_confirmed', line: `The invitation has not been confirmed as sent (${why}). The case shows whether it went; if it is still held, press Send the invitation now there. ` };
}
